import React from "react";
import { Heart, Gem, Sparkles, QrCode, X as XIcon } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useWishlist } from "@/context/WishlistContext";
import { useRates } from "@/context/RatesContext";
import { computePrice, fmtINR } from "@/lib/pricing";
import { fileUrl } from "@/lib/api";

const SHOWROOM_WHATSAPP = "917814854580"; // Bholaa Jewels — Chandigarh

export default function ProductCard({ item, onOpen, index = 0 }) {
  const { rates, gstOn } = useRates();
  const { has, toggle } = useWishlist();
  const [pop, setPop] = React.useState(false);
  const [imgIdx, setImgIdx] = React.useState(0);
  const [qrOpen, setQrOpen] = React.useState(false);
  const price = computePrice(item, rates, gstOn);
  const active = has(item.tag_no);
  const isRingstudio = item.collection === "ringstudio";
  const gallery = (item.images || []).filter(Boolean);
  const mainImg = fileUrl(gallery[imgIdx] || item.image_path);

  // Build the WhatsApp link that the QR will encode. Opens chat to the
  // showroom with a pre-filled enquiry containing tag + live price.
  const waLink = React.useMemo(() => {
    const msg = `Hi Bholaa Jewels, I'd like to know more about *${item.item_name}* (Tag: ${item.tag_no}, ${item.karat}). Price shown: ${fmtINR(price.final)}.`;
    return `https://wa.me/${SHOWROOM_WHATSAPP}?text=${encodeURIComponent(msg)}`;
  }, [item.item_name, item.tag_no, item.karat, price.final]);

  // Auto-cycle through up to 3 images on hover (desktop) for Ringstudio
  const onEnter = () => {
    if (!isRingstudio || gallery.length < 2) return;
    let i = 0;
    const id = setInterval(() => {
      i = (i + 1) % Math.min(gallery.length, 3);
      setImgIdx(i);
    }, 900);
    return () => clearInterval(id);
  };

  React.useEffect(() => { setImgIdx(0); }, [item.tag_no]);

  const [cleanup, setCleanup] = React.useState(null);
  const mouseEnter = () => setCleanup(() => onEnter());
  const mouseLeave = () => { if (cleanup) cleanup(); setImgIdx(0); };

  const handleHeart = (e) => {
    e.stopPropagation();
    setPop(true);
    setTimeout(() => setPop(false), 400);
    toggle(item);
  };

  const handleQR = (e) => {
    e.stopPropagation();
    setQrOpen(true);
  };

  return (
    <div
      className={`group relative bg-white border ${isRingstudio ? "border-rose-gold/25" : "border-rose-gold/10"} hover:border-gold hover:shadow-xl card-lift cursor-pointer fade-up`}
      style={{ animationDelay: `${Math.min(index, 16) * 40}ms` }}
      onClick={() => onOpen?.(item)}
      onMouseEnter={mouseEnter}
      onMouseLeave={mouseLeave}
      data-testid={`product-card-${item.tag_no}`}
    >
      {isRingstudio && (
        <div className="absolute top-3 left-3 z-10 px-2.5 py-1 bg-navy-900/85 text-gold text-[10px] tracking-[0.25em] uppercase border border-gold/50 flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Ringstudio
        </div>
      )}
      <div className="aspect-[4/5] overflow-hidden bg-gradient-to-br from-ivory-warm to-ivory-soft relative">
        {mainImg ? (
          <img
            src={mainImg}
            alt={item.item_name}
            className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gold/30">
            <Gem className="w-20 h-20" strokeWidth={0.75} />
          </div>
        )}
        <button
          onClick={handleHeart}
          className={`absolute top-3 right-3 p-2 bg-white/90 backdrop-blur-sm border border-gold/30 hover:border-gold ${pop ? "heart-active" : ""}`}
          data-testid={`wishlist-heart-${item.tag_no}`}
        >
          <Heart className={`w-4 h-4 transition-colors ${active ? "text-gold fill-gold" : "text-navy-900/50"}`} />
        </button>
        <button
          onClick={handleQR}
          aria-label={`Scan to WhatsApp enquiry for ${item.tag_no}`}
          className="absolute top-3 right-14 p-2 bg-white/90 backdrop-blur-sm border border-gold/30 hover:border-gold hover:bg-gold/10 transition-colors"
          data-testid={`product-qr-${item.tag_no}`}
        >
          <QrCode className="w-4 h-4 text-navy-900/70" />
        </button>
        {!isRingstudio && item.diamond_weight > 0 && (
          <div className="absolute top-3 left-3 px-2 py-1 bg-navy-900/85 text-gold text-[10px] tracking-[0.25em] uppercase border border-gold/40">Diamond</div>
        )}
        {isRingstudio && gallery.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {gallery.slice(0, 4).map((g, i) => (
              <span key={`${item.tag_no}-dot-${g || i}`} className={`w-1.5 h-1.5 rounded-full ${i === imgIdx ? "bg-gold" : "bg-white/60"}`} />
            ))}
          </div>
        )}
      </div>
      <div className="p-5 space-y-2 bg-white">
        <div className="flex items-center justify-between">
          <span className="overline text-gold-dark">{item.tag_no}</span>
          <span className="overline text-navy-900/60">{item.karat}</span>
        </div>
        <h3 className="font-display text-lg leading-snug line-clamp-2 min-h-[3.5rem]" style={{ color: '#1a0f00', fontWeight: 500 }}>{item.item_name}</h3>
        <div className="flex items-baseline justify-between pt-2 border-t border-gold/20">
          <span className="text-[11px] tracking-widest uppercase text-navy-900/60">{price.mrpMode ? "MRP" : "From"}</span>
          <span className="font-display text-xl text-navy-900" style={{ fontWeight: 600 }}>{fmtINR(price.final)}</span>
        </div>
      </div>

      {qrOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4"
          onClick={(e) => { e.stopPropagation(); setQrOpen(false); }}
          data-testid={`qr-modal-${item.tag_no}`}
        >
          <div
            className="bg-white max-w-sm w-full p-6 md:p-7 relative border border-gold/30 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={(e) => { e.stopPropagation(); setQrOpen(false); }}
              className="absolute top-3 right-3 p-1.5 hover:bg-gold/10"
              aria-label="Close QR"
              data-testid={`qr-close-${item.tag_no}`}
            >
              <XIcon className="w-4 h-4" style={{ color: "#0B1A33" }} />
            </button>
            <div className="text-center">
              <div className="overline" style={{ color: "#A88934" }}>Scan & Save · WhatsApp</div>
              <div className="font-display text-xl mt-2 mb-1" style={{ color: "#0B1A33", fontWeight: 600 }}>{item.item_name}</div>
              <div className="text-xs tracking-widest uppercase" style={{ color: "rgba(11,26,51,0.6)" }}>{item.tag_no} · {item.karat}</div>
              <div className="my-5 flex items-center justify-center">
                <div className="p-3 bg-white border border-gold/40">
                  <QRCodeSVG value={waLink} size={196} bgColor="#FFFFFF" fgColor="#0B1A33" level="M" />
                </div>
              </div>
              <div className="font-display text-2xl" style={{ color: "#0B1A33", fontWeight: 600 }}>{fmtINR(price.final)}</div>
              <p className="text-xs mt-3 leading-relaxed" style={{ color: "rgba(11,26,51,0.65)" }}>
                Scan with your phone&apos;s camera to open WhatsApp pre-filled with this piece&apos;s details and today&apos;s price.
              </p>
              <a
                href={waLink}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 text-xs tracking-widest uppercase font-medium"
                style={{ background: "#25D366", color: "#FFFFFF" }}
                data-testid={`qr-open-whatsapp-${item.tag_no}`}
              >
                Open in WhatsApp
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
